import type { MenuItem } from "@replo/design-system/components/menu/Menu";

import * as React from "react";

import { Menu, MenuTrigger } from "@replo/design-system/components/menu/Menu";
import {
  SidebarMenuButton,
  useSidebar,
} from "@replo/design-system/components/shadcn/core/sidebar";
import { useIsMobile } from "@replo/design-system/components/shadcn/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { Link, useLocation } from "react-router-dom";

export type DashboardMenuItemContent = {
  title: string;
  onClick?: () => void;
  path?: string;
  additionalPath?: string;
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
  href?: string;
  className?: string;
  textClassName?: string;
  isSelected?: boolean;
  hasSubItems?: boolean;
  menuItems?: MenuItem[];
  target?: string;
  customWidth?: number;
};

type DashboardMenuItemProps = {
  item: DashboardMenuItemContent;
  nestedMenuAlign?: "start" | "end";
  nestedMenuAlignOffset?: number;
};

type MenuItemContentProps = {
  startEnhancer?: React.ReactNode;
  title: string;
  textClassNames: string;
  endEnhancer?: React.ReactNode;
};

const DashboardMenuItem: React.FC<DashboardMenuItemProps> = ({
  item: {
    title,
    onClick,
    path,
    startEnhancer,
    href,
    endEnhancer,
    className,
    textClassName,
    hasSubItems,
    menuItems,
    target,
    customWidth,
  },
  nestedMenuAlign = "end",
  nestedMenuAlignOffset = 0,
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();
  const isSelected = Boolean(path && location.pathname.startsWith(path));

  const itemTextClassName = twMerge(
    "text-left w-full text-base",
    textClassName,
  );
  const { open, openMobile } = useSidebar();
  const isMobile = useIsMobile();
  const isSidebarExpanded = isMobile ? openMobile : open;

  const itemClassName = twMerge(
    "flex gap-2 justify-start items-center text-default hover:bg-menu-item-hover p-1.5 cursor-pointer rounded-md",
    (path || href) &&
      isSelected &&
      !hasSubItems &&
      (isSidebarExpanded
        ? "bg-surface text-default hover:bg-border font-medium"
        : "font-medium"),
    isMenuOpen && "bg-button-tertiary-hover",
    hasSubItems && isSelected && "font-medium",
    className,
  );

  let collapsedClassName: string | undefined;
  if (!isSidebarExpanded) {
    if (isSelected) {
      collapsedClassName =
        "bg-default text-white hover:bg-button-primary-hover";
    } else {
      collapsedClassName =
        "border-border border-0.5 hover:bg-sheet text-muted hover:text-default";
    }
  } else {
    collapsedClassName = undefined;
  }

  const sidebarButtonClassName = twMerge(
    "p-0",
    isSelected &&
      (!isSidebarExpanded
        ? "data-[active=true]:bg-default data-[active=true]:text-white data-[active=true]:hover:bg-button-primary-hover data-[active=true]:hover:text-white"
        : "data-[active=true]:bg-surface data-[active=true]:text-default data-[active=true]:hover:bg-border data-[active=true]:hover:text-default"),
  );

  if (href) {
    return (
      <SidebarMenuButton
        asChild
        className={sidebarButtonClassName}
        tooltip={title}
        isActive={isSelected}
      >
        <Link
          className={twMerge(itemClassName, collapsedClassName)}
          to={href}
          target={target}
          onClick={onClick}
        >
          <MenuItemContent
            textClassNames={itemTextClassName}
            startEnhancer={startEnhancer}
            endEnhancer={endEnhancer}
            title={title}
          />
        </Link>
      </SidebarMenuButton>
    );
  }

  if (menuItems) {
    return (
      <Menu
        items={menuItems}
        isOpen={isMenuOpen}
        customWidth={customWidth}
        onRequestClose={() => setIsMenuOpen(false)}
        onRequestOpen={() => setIsMenuOpen(true)}
        side="right"
        sideOffset={isSidebarExpanded ? 16 : 20}
        align={nestedMenuAlign ?? "end"}
        alignOffset={nestedMenuAlignOffset ?? 0}
        triggerAsChild
        size="base"
        trigger={
          <MenuTrigger
            className={twMerge(
              "w-full rounded-md",
              !isSidebarExpanded && "w-fit",
            )}
          >
            <SidebarMenuButton
              asChild
              className={sidebarButtonClassName}
              tooltip={title}
              isActive={isSelected}
            >
              <div className={twMerge(itemClassName, collapsedClassName)}>
                <MenuItemContent
                  textClassNames={itemTextClassName}
                  startEnhancer={startEnhancer}
                  endEnhancer={endEnhancer}
                  title={title}
                />
              </div>
            </SidebarMenuButton>
          </MenuTrigger>
        }
      />
    );
  }

  return (
    <SidebarMenuButton
      asChild
      className={sidebarButtonClassName}
      tooltip={title}
      isActive={isSelected}
    >
      <button
        className={twMerge(itemClassName, collapsedClassName)}
        onClick={onClick}
      >
        <MenuItemContent
          textClassNames={itemTextClassName}
          startEnhancer={startEnhancer}
          endEnhancer={endEnhancer}
          title={title}
        />
      </button>
    </SidebarMenuButton>
  );
};

const MenuItemContent: React.FC<MenuItemContentProps> = ({
  startEnhancer,
  title,
  textClassNames,
  endEnhancer,
}) => (
  <>
    {startEnhancer && (
      <div className={twMerge("flex h-4 w-4 items-center justify-center")}>
        {startEnhancer}
      </div>
    )}
    <TruncatedText text={title} className={textClassNames} />
    {endEnhancer && endEnhancer}
  </>
);

const TruncatedText = ({
  text,
  className,
}: {
  text: string;
  className?: string;
}) => {
  return (
    <div className="my-auto inline-grid w-full">
      <span className={twMerge("truncate", className)}>{text}</span>
    </div>
  );
};

export default DashboardMenuItem;
